/**
 * Leads Async Thunks
 * All async actions for leads operations
 */

import { createAsyncThunk } from '@reduxjs/toolkit';
import * as leadsApi from '../api/leadsApi';
import * as googleApi from '../api/googleApi';
import * as phoneApi from '../api/phoneApi';

/**
 * Fetch leads with filters (checks for assigneeFilter to determine API endpoint)
 */
export const fetchLeadsThunk = createAsyncThunk(
  'leads/fetchLeads',
  async (params, { rejectWithValue }) => {
    try {
      const { assigneeFilter, ...restParams } = params || {};
      let data;

      if (assigneeFilter) {
        data = await leadsApi.searchLeadsByAssignee({ assigneeFilter, ...restParams });
      } else {
        data = await leadsApi.fetchLeads(restParams);
      }

      return {
        items: data.leads || [],
        totalCount: data.total || 0,
      };
    } catch (error) {
      console.error('Error fetching leads:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/**
 * Fetch single lead by ID
 */
export const fetchLeadByIdThunk = createAsyncThunk(
  'leads/fetchLeadById',
  async ({ id, patientStatus }, { rejectWithValue }) => {
    try {
      const data = await leadsApi.fetchLead(id, patientStatus);
      return data;
    } catch (error) {
      console.error('Error fetching lead:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/**
 * Create new lead
 */
export const createLeadThunk = createAsyncThunk(
  'leads/createLead',
  async (data, { rejectWithValue }) => {
    try {
      const result = await leadsApi.createLead(data);
      return result;
    } catch (error) {
      console.error('Error creating lead:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/**
 * Update lead
 */
export const updateLeadThunk = createAsyncThunk(
  'leads/updateLead',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const result = await leadsApi.updateLead(id, data);
      return result;
    } catch (error) {
      console.error('Error updating lead:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/**
 * Delete lead
 */
export const deleteLeadThunk = createAsyncThunk(
  'leads/deleteLead',
  async (id, { rejectWithValue }) => {
    try {
      const data = await leadsApi.deleteLead(id);
      return data;
    } catch (error) {
      console.error('Error deleting lead:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/**
 * Toggle favourite status
 */
export const toggleFavouriteThunk = createAsyncThunk(
  'leads/toggleFavourite',
  async (id, { rejectWithValue }) => {
    try {
      const data = await leadsApi.toggleFavourite(id);
      return data;
    } catch (error) {
      console.error('Error toggling favourite:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/**
 * Mark lead as read
 */
export const markAllReadThunk = createAsyncThunk(
  'leads/markAllRead',
  async (id, { rejectWithValue }) => {
    try {
      const data = await leadsApi.markLeadAsRead(id);
      return data;
    } catch (error) {
      console.error('Error marking lead as read:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/**
 * Send message (email or SMS)
 */
export const sendMessageThunk = createAsyncThunk(
  'leads/sendMessage',
  async ({ medium, leadId, to, text, subject, formData }, { rejectWithValue }) => {
    try {
      let data;

      if (medium === 'email') {
        data = await googleApi.sendEmail(formData);
      } else if (medium === 'sms') {
        data = await phoneApi.sendSMS({ to, text });
      } else {
        throw new Error('Invalid medium. Must be "email" or "sms".');
      }

      return data;
    } catch (error) {
      console.error('Error sending message:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/**
 * Delete message
 */
export const deleteMessageThunk = createAsyncThunk(
  'leads/deleteMessage',
  async ({ id, dateTime }, { rejectWithValue }) => {
    try {
      const data = await leadsApi.deleteMessage(id, dateTime);
      return data;
    } catch (error) {
      console.error('Error deleting message:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/**
 * Mark message as unread
 */
export const markMessageUnreadThunk = createAsyncThunk(
  'leads/markMessageUnread',
  async ({ id, dateTime }, { rejectWithValue }) => {
    try {
      const data = await leadsApi.markMessageAsUnread(id, dateTime);
      return data;
    } catch (error) {
      console.error('Error marking message as unread:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/**
 * Promote lead to patient
 */
export const promoteLeadThunk = createAsyncThunk(
  'leads/promoteLead',
  async (id, { rejectWithValue }) => {
    try {
      const data = await leadsApi.promoteLead(id);
      return data;
    } catch (error) {
      console.error('Error promoting lead:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/**
 * Update onboarding checklist
 */
export const updateChecklistThunk = createAsyncThunk(
  'leads/updateChecklist',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const result = await leadsApi.updateOnboardingChecklist(id, data);
      return result;
    } catch (error) {
      console.error('Error updating checklist:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/**
 * Bulk update status for multiple leads
 */
export const bulkUpdateStatusThunk = createAsyncThunk(
  'leads/bulkUpdateStatus',
  async ({ ids, status }, { rejectWithValue }) => {
    try {
      const results = await Promise.allSettled(
        ids.map(id => leadsApi.updateLead(id, { status }))
      );

      const succeeded = results.filter(r => r.status === 'fulfilled').map(r => r.value);
      const failed = results.filter(r => r.status === 'rejected').map(r => r.reason);

      return { succeeded, failed };
    } catch (error) {
      console.error('Error bulk updating status:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/**
 * Bulk delete leads
 */
export const bulkDeleteThunk = createAsyncThunk(
  'leads/bulkDelete',
  async (ids, { rejectWithValue }) => {
    try {
      const results = await Promise.allSettled(
        ids.map(id => leadsApi.deleteLead(id))
      );

      const succeeded = results.filter(r => r.status === 'fulfilled').map(r => r.value);
      const failed = results.filter(r => r.status === 'rejected').map(r => r.reason);

      return { succeeded, failed };
    } catch (error) {
      console.error('Error bulk deleting leads:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/**
 * Bulk archive leads
 */
export const bulkArchiveThunk = createAsyncThunk(
  'leads/bulkArchive',
  async (ids, { rejectWithValue }) => {
    try {
      const results = await Promise.allSettled(
        ids.map(id => leadsApi.updateLead(id, { archived: true }))
      );

      const succeeded = results.filter(r => r.status === 'fulfilled').map(r => r.value);
      const failed = results.filter(r => r.status === 'rejected').map(r => r.reason);

      return { succeeded, failed };
    } catch (error) {
      console.error('Error bulk archiving leads:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/**
 * Bulk mark leads as junk
 */
export const bulkJunkThunk = createAsyncThunk(
  'leads/bulkJunk',
  async (ids, { rejectWithValue }) => {
    try {
      const results = await Promise.allSettled(
        ids.map(id => leadsApi.updateLead(id, { junk: true }))
      );

      const succeeded = results.filter(r => r.status === 'fulfilled').map(r => r.value);
      const failed = results.filter(r => r.status === 'rejected').map(r => r.reason);

      return { succeeded, failed };
    } catch (error) {
      console.error('Error bulk junking leads:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/**
 * Bulk mark leads as read
 */
export const bulkMarkReadThunk = createAsyncThunk(
  'leads/bulkMarkRead',
  async (ids, { rejectWithValue }) => {
    try {
      const results = await Promise.allSettled(
        ids.map(id => leadsApi.updateLatestReadStatus(id))
      );

      const succeeded = results.filter(r => r.status === 'fulfilled').map(r => r.value);
      const failed = results.filter(r => r.status === 'rejected').map(r => r.reason);

      return { succeeded, failed };
    } catch (error) {
      console.error('Error bulk marking read:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/**
 * Merge multiple leads
 */
export const mergeLeadsThunk = createAsyncThunk(
  'leads/mergeLeads',
  async ({ primaryLeadId, secondaryLeadIds }, { rejectWithValue }) => {
    try {
      const data = await leadsApi.mergeLeads(primaryLeadId, secondaryLeadIds);
      return data;
    } catch (error) {
      console.error('Error merging leads:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/**
 * Convert an onboarding lead to a full patient
 */
export const convertOnboardingLeadThunk = createAsyncThunk(
  'leads/convertOnboardingLead',
  async (id, { rejectWithValue }) => {
    try {
      const data = await leadsApi.convertOnboardingLead(id);
      return { ...data, leadId: id };
    } catch (error) {
      console.error('Error converting lead:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/**
 * Send onboarding form (intake or consent) via email
 */
export const sendOnboardingFormThunk = createAsyncThunk(
  'leads/sendOnboardingForm',
  async ({ id, formType }, { rejectWithValue }) => {
    try {
      const data = await leadsApi.sendOnboardingForm(id, formType);
      return data;
    } catch (error) {
      console.error('Error sending onboarding form:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/**
 * Fetch onboarding stage leads (uses dedicated /api/leads/onboarding endpoint)
 */
export const fetchOnboardingLeadsThunk = createAsyncThunk(
  'leads/fetchOnboardingLeads',
  async (params, { rejectWithValue }) => {
    try {
      const data = await leadsApi.fetchOnboardingLeads(params);
      return data.leads || [];
    } catch (error) {
      console.error('Error fetching onboarding leads:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);
